import MapboxConfig from "@/plugins/mapbox";
import mapboxgl from "mapbox-gl";
import store from "@/store";

let map;
let hoveredStateIdShorelines = null;

const colormap = {
  NSM: [
    "interpolate",
    ["linear"],
    ["get", "NSM"],
    -70,
    "#ca0020",
    -50,
    "#df5251",
    -30,
    "#f4a582",
    -10,
    "#f6cebd",
    10,
    "#f7f7f7",
    40,
    "#c5deeb",
    70,
    "#92c5de",
    100,
    "#4b9bc7",
    150,
    "#0571b0"
  ],
  WLR: [
    "interpolate",
    ["linear"],
    ["get", "WLR"],
    -10,
    "#ca0020",
    -3,
    "#df5251",
    -2,
    "#f4a582",
    -1,
    "#f6cebd",
    -0.5,
    "#f7f7f7",
    0.5,
    "#c5deeb",
    1,
    "#92c5de",
    2,
    "#4b9bc7",
    4,
    "#0571b0"
  ],
  sl_class: [
    "match",
    ["get", "sl_class"],
    "artificial accumulation (constructions)",
    "black",
    "stable, no significant change",
    "grey",
    "significant accumulation",
    "blue",
    "significant erosion",
    "red",
    "white"
  ]
};

const MapServices = {
  initMap() {
    mapboxgl.accessToken = MapboxConfig.accessToken;
    map = new mapboxgl.Map({
      container: "map", // container id
      style: MapboxConfig.style, // style URL
      center: [-58, 7.07], // starting position [lng, lat]
      zoom: 9 // starting zoom
    });

    map.addControl(new mapboxgl.ScaleControl());
    map.addControl(new mapboxgl.NavigationControl());
    map.addControl(new mapboxgl.FullscreenControl());

    map.on("load", () => {
      map.loadImage("/static/icons/arrow-up.png", function(error, image) {
        if (error) throw error;
        map.addImage("arrow-marker", image, {
          sdf: "true"
        });
      });
      map.addSource("dsas_source", {
        type: "geojson",
        data: "/static/geodata/results_split.geojson",
        promoteId: "OBJECTID"
      });
      map.addSource("shorelines_source", {
        type: "geojson",
        data: "/static/geodata/shorelines_guyana.geojson",
        promoteId: "OBJECTID"
      });
      map.addSource("bathy_2015-09-19_source", {
        type: "geojson",
        data: "/static/geodata/bathy/bathy_2015-09-19.geojson",
        generateId: true
      });
      map.addSource("bathy_2020-09-17_source", {
        type: "geojson",
        data: "/static/geodata/bathy/bathy_2020-09-17.geojson",
        generateId: true
      });
      this.addCurrentSource("2020-07");
      store.commit("map/DATA_LOADED", true);
    });
  },
  addShorelines() {
    map.addLayer({
      id: "shorelines_layer",
      type: "line",
      source: "shorelines_source",
      layout: {},
      paint: {
        "line-color": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          "red",
          [
            "interpolate",
            ["linear"],
            ["get", "date_index"],
            1,
            "#fde725",
            10,
            "#238a8d",
            19,
            "#450d54"
          ]
        ],
        "line-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          1,
          0.4
        ],
        "line-width": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          4,
          3
        ]
      }
    });

    map.on("mousemove", "shorelines_layer", function(e) {
      if (e.features.length > 0) {
        if (hoveredStateIdShorelines) {
          map.setFeatureState(
            { source: "shorelines_source", id: hoveredStateIdShorelines },
            { hover: false }
          );
        }
        hoveredStateIdShorelines = e.features[0].id;
        store.commit("map/SET_HOVERED_DATE", e.features[0].properties.date);
        map.setFeatureState(
          { source: "shorelines_source", id: hoveredStateIdShorelines },
          { hover: true }
        );
      }
    });

    map.on("mouseenter", "shorelines_layer", function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // When the mouse leaves the state-fill layer, update the feature state of the
    // previously hovered feature.
    map.on("mouseleave", "shorelines_layer", function() {
      if (hoveredStateIdShorelines) {
        map.setFeatureState(
          { source: "shorelines_source", id: hoveredStateIdShorelines },
          { hover: false }
        );
      }
      hoveredStateIdShorelines = null;
      store.commit("map/SET_HOVERED_DATE", null);
      map.getCanvas().style.cursor = "";
    });
  },
  addResults(layer) {
    map.addLayer({
      id: layer.name,
      type: "line",
      source: "dsas_source",
      layout: {},
      paint: {
        "line-color": colormap[layer.name],
        "line-opacity": 1,
        "line-width": 5
      }
    });
    map.on("click", layer.name, function(e) {
      const properties = e.features[0].properties;

      let popUpHtml = `${layer.title}: <b>${properties[layer.name]} m</b>`;
      if (layer.showError) {
        popUpHtml += `
           <br>R²: <b>${properties.WR2}</b><br>
           Standard Error: <b>${properties.WSE} m</b><br>
           90% Confidence Interval: <b>${properties.WCI90}</b>`;
      }

      new mapboxgl.Popup()
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .setHTML(popUpHtml)
        .addTo(map);
    });

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on("mouseenter", layer.name, function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", layer.name, function() {
      map.getCanvas().style.cursor = "";
    });
  },
  removeLayer(name) {
    map.removeLayer(name);
  },
  addCurrentSource(date_string) {
    map.addSource(`currents_${date_string}_source`, {
      type: "geojson",
      data: `/static/geodata/currents/${date_string}.geojson`,
      generateId: true
    });
  },
  addCurrentLayer(date_string) {
    map.addLayer({
      id: `currents_${date_string}_layer`,
      type: "symbol",
      source: `currents_${date_string}_source`,
      layout: {
        "icon-image": "arrow-marker",
        "icon-rotate": ["get", "dir_degree"],
        "icon-rotation-alignment": "map",
        "icon-size": 0.6
      },
      paint: {
        "icon-color": [
          "interpolate",
          ["linear"],
          ["get", "magni_m"],
          0.4,
          "white",
          1.5,
          "red"
        ]
      }
    });

    map.on("click", `currents_${date_string}_layer`, function(e) {
      new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(
          `Direction [°]: <b>${e.features[0].properties.dir_degree}</b><br>Velocity [m/s]: <b>${e.features[0].properties.magni_m}</b>`
        )
        .addTo(map);
    });

    map.on("mouseenter", `currents_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", `currents_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "";
      //  test
    });
  },
  async loadAddCurrentLayer(date_string) {
    if (map.getSource(`currents_${date_string}_source`)) {
      this.addCurrentLayer(date_string);
    } else {
      await this.addCurrentSource(date_string);
      this.addCurrentLayer(date_string);
    }
  },

  addWaveSource(date_string) {
    map.addSource(`waves_${date_string}_source`, {
      type: "geojson",
      data: `/static/geodata/waves/${date_string}.geojson`,
      generateId: true
    });
  },
  addWaveLayer(date_string) {
    map.addLayer({
      id: `waves_${date_string}_layer`,
      type: "symbol",
      source: `waves_${date_string}_source`,
      layout: {
        "icon-image": "arrow-marker",
        "icon-rotate": ["get", "wavedir"],
        "icon-rotation-alignment": "map",
        "icon-size": 0.6
      },
      paint: {
        "icon-color": [
          "interpolate",
          ["linear"],
          ["get", "sigWave"],
          0.5,
          "white",
          2.7,
          "red"
        ]
      }
    });

    map.on("click", `waves_${date_string}_layer`, function(e) {
      new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(
          `Mean wave direction [°]: <b>${e.features[0].properties.wavedir}</b><br>Spectral significant wave height [m]: <b>${e.features[0].properties.sigWave}</b>`
        )
        .addTo(map);
    });

    map.on("mouseenter", `waves_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", `waves_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "";
      //  test
    });
  },
  async loadAddWaveLayer(date_string) {
    if (map.getSource(`waves_${date_string}_source`)) {
      this.addWaveLayer(date_string);
    } else {
      await this.addWaveSource(date_string);
      this.addWaveLayer(date_string);
    }
  },

  addBathy(date_string) {
    map.addLayer({
      id: `bathy_${date_string}_layer`,
      type: "line",
      source: `bathy_${date_string}_source`,
      layout: {},
      paint: {
        "line-color": [
          "interpolate",
          ["linear"],
          ["get", "Elevation"],
          -10,
          "#0000FF",
          -8,
          "#3661FF",
          -6,
          "#38ACFF",
          -4,
          "#00FFFF",
          -3,
          "#AAFF00",
          -2.5,
          "#FFFF00",
          -2,
          "#FFAA00",
          -1.5,
          "#E64C00",
          -1,
          "#E60000",
          -0.5,
          "#E80000"
        ],
        "line-width": 2
      }
    });

    map.on("click", `bathy_${date_string}_layer`, function(e) {
      new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(`Elevation [m]: <b>${e.features[0].properties.Elevation}`)
        .addTo(map);
    });

    map.on("mouseenter", `bathy_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "pointer";
    });

    // Change it back to a pointer when it leaves.
    map.on("mouseleave", `bathy_${date_string}_layer`, function() {
      map.getCanvas().style.cursor = "";
      //  test
    });
  }
};

export default MapServices;
